import { z } from 'zod';
import { CONVERSATION_PRIORITIES, type ConversationPriority } from './contract';

/**
 * The SLA contract (docs/specs/010-canais-sla-e-consentimento/spec.md, rules 21–28, and
 * docs/specs/045 for the pause and the next response): the policies, the cycle a conversation
 * keeps (when it started, the deadlines, when each target was met, whether it is paused), and
 * the pure rules — which policy applies, the deadlines, the state at a moment. The mocked API
 * and the screens use these same functions.
 */

/** `paused` (spec 045): the conversation waits for the contact; no deadline runs. */
export const SLA_STATES = ['on_track', 'at_risk', 'breached', 'met', 'paused'] as const;
export type SlaState = (typeof SLA_STATES)[number];

/** The states the list filters by (rule 27). */
export const SLA_FILTERS = ['at_risk', 'breached'] as const;
export type SlaFilter = (typeof SLA_FILTERS)[number];

/** In the order the badge speaks of them: the next pending one wins (spec 045). */
export const SLA_TARGETS = ['first-response', 'next-response', 'resolution'] as const;
export type SlaTargetKind = (typeof SLA_TARGETS)[number];

export const SLA_LIMITS = {
  nameMin: 2,
  nameMax: 60,
  minutesMin: 1,
  /** 30 days. */
  minutesMax: 43_200,
  atRiskMin: 50,
  atRiskMax: 95,
  atRiskDefault: 80,
  policiesMax: 30,
} as const;

export const SLA_ERROR_CODES = [
  'SLA_POLICY_NOT_FOUND',
  'SLA_POLICY_INVALID',
  'SLA_POLICY_NAME_TAKEN',
] as const;

/* ------------------------------------------------------------------ shapes */

export interface SlaPolicy {
  id: string;
  name: string;
  active: boolean;
  /** 0 first: the first active policy whose conditions match applies (rule 21). */
  position: number;
  /** Empty: any. */
  queueIds: string[];
  connectionIds: string[];
  priorities: ConversationPriority[];
  firstResponseMinutes: number | null;
  resolutionMinutes: number | null;
  /** Spec 045: each contact message after the first response asks for an answer in this time. */
  nextResponseMinutes: number | null;
  useBusinessHours: boolean;
  /** Spec 045: the cycle stops while the conversation is pending or snoozed. */
  pauseWhilePending: boolean;
  atRiskPercent: number;
  createdAt: string;
  updatedAt: string;
}

export interface SlaTarget {
  dueAt: string;
  atRiskAt: string;
  completedAt: string | null;
  state: SlaState;
}

export interface ConversationSla {
  policy: { id: string; name: string };
  state: SlaState;
  startedAt: string;
  /** The next target still pending (first response, next response, resolution, in that order). */
  target: SlaTargetKind | null;
  dueAt: string | null;
  atRiskAt: string | null;
  firstResponse: SlaTarget | null;
  nextResponse: SlaTarget | null;
  resolution: SlaTarget | null;
  /** Since when the cycle waits for the contact; null = running (spec 045). */
  pausedAt: string | null;
}

/**
 * What a conversation keeps of its current cycle (rule 22): when it started and when each target
 * was met, the deadlines of the policy that applied (recomputed by `planSlaCycle`), and the
 * pause (spec 045).
 */
export interface SlaCycle {
  policyId: string | null;
  startedAt: string;
  firstResponseDueAt: string | null;
  firstResponseAtRiskAt: string | null;
  firstRespondedAt: string | null;
  nextResponseDueAt: string | null;
  nextResponseAtRiskAt: string | null;
  nextRespondedAt: string | null;
  resolutionDueAt: string | null;
  resolutionAtRiskAt: string | null;
  resolvedAt: string | null;
  pausedAt: string | null;
  pausedSeconds: number;
  /** The notifications already sent this cycle (`<target>:<state>`, rule 26). */
  notified: string[];
}

/* ------------------------------------------------------------------ schemas */

const minutes = z.number().int().min(SLA_LIMITS.minutesMin).max(SLA_LIMITS.minutesMax).nullable();

export const slaPolicyInputSchema = z
  .object({
    name: z.string().trim().min(SLA_LIMITS.nameMin).max(SLA_LIMITS.nameMax),
    active: z.boolean(),
    queueIds: z.array(z.string().min(1)),
    connectionIds: z.array(z.string().min(1)),
    priorities: z.array(z.enum(CONVERSATION_PRIORITIES)),
    firstResponseMinutes: minutes,
    resolutionMinutes: minutes,
    nextResponseMinutes: minutes.optional(),
    useBusinessHours: z.boolean(),
    pauseWhilePending: z.boolean().optional(),
    atRiskPercent: z.number().int().min(SLA_LIMITS.atRiskMin).max(SLA_LIMITS.atRiskMax),
  })
  .strict();
export type SlaPolicyInput = z.input<typeof slaPolicyInputSchema>;

/** Rule 21: at least one target (the next response counts, spec 045). */
export function hasSlaTarget(input: {
  firstResponseMinutes: number | null;
  resolutionMinutes: number | null;
  nextResponseMinutes?: number | null;
}): boolean {
  return (
    input.firstResponseMinutes !== null ||
    input.resolutionMinutes !== null ||
    (input.nextResponseMinutes ?? null) !== null
  );
}

export const moveSlaPolicySchema = z.object({ direction: z.enum(['up', 'down']) }).strict();

/* ------------------------------------------------------------------ rules */

/** Rule 21: the first active policy (by position) whose non-empty conditions all match. */
export function pickSlaPolicy<
  P extends Pick<SlaPolicy, 'active' | 'position' | 'queueIds' | 'connectionIds' | 'priorities'>,
>(
  policies: readonly P[],
  conversation: { queueId: string | null; connectionId: string; priority: ConversationPriority },
): P | null {
  return (
    policies
      .filter((policy) => policy.active)
      .toSorted((a, b) => a.position - b.position)
      .find(
        (policy) =>
          (policy.queueIds.length === 0 ||
            (conversation.queueId !== null && policy.queueIds.includes(conversation.queueId))) &&
          (policy.connectionIds.length === 0 ||
            policy.connectionIds.includes(conversation.connectionId)) &&
          (policy.priorities.length === 0 || policy.priorities.includes(conversation.priority)),
      ) ?? null
  );
}

/** Adds minutes — business minutes with the calendar, clock minutes without (rule 23). */
export type AddMinutes = (from: Date, minutes: number) => Date | null;

/**
 * Rule 23: the deadlines and the risk instants of a cycle under a policy, from its start; the
 * targets already met stay as they were. The next response is planned by a contact message
 * (`planNextResponse`), not here.
 */
export function planSlaCycle(
  cycle: SlaCycle,
  policy: Pick<
    SlaPolicy,
    'id' | 'firstResponseMinutes' | 'resolutionMinutes' | 'atRiskPercent'
  > | null,
  addMinutes: AddMinutes,
): SlaCycle {
  if (!policy) {
    return {
      ...cycle,
      policyId: null,
      firstResponseDueAt: null,
      firstResponseAtRiskAt: null,
      nextResponseDueAt: null,
      nextResponseAtRiskAt: null,
      resolutionDueAt: null,
      resolutionAtRiskAt: null,
    };
  }
  const start = new Date(cycle.startedAt);
  const at = (total: number | null, share = 1) => {
    if (total === null) return null;
    return addMinutes(start, Math.max(1, Math.round(total * share)))?.toISOString() ?? null;
  };
  const risk = policy.atRiskPercent / 100;
  return {
    ...cycle,
    policyId: policy.id,
    firstResponseDueAt: at(policy.firstResponseMinutes),
    firstResponseAtRiskAt: at(policy.firstResponseMinutes, risk),
    resolutionDueAt: at(policy.resolutionMinutes),
    resolutionAtRiskAt: at(policy.resolutionMinutes, risk),
  };
}

/**
 * Spec 045, rule 7: a contact message after the first response plans the next response from
 * that instant; nothing without a target, before the first response, or while a previous
 * contact message still waits for an answer.
 */
export function planNextResponse(
  cycle: SlaCycle,
  policy: Pick<SlaPolicy, 'nextResponseMinutes' | 'atRiskPercent'>,
  at: Date,
  addMinutes: AddMinutes,
): SlaCycle {
  if (policy.nextResponseMinutes === null) return cycle;
  if (cycle.firstRespondedAt === null) return cycle;
  if (cycle.nextResponseDueAt !== null && cycle.nextRespondedAt === null) return cycle;
  const total = policy.nextResponseMinutes;
  const due = addMinutes(at, Math.max(1, total));
  const atRisk = addMinutes(at, Math.max(1, Math.round((total * policy.atRiskPercent) / 100)));
  if (!due || !atRisk) return cycle;
  return {
    ...cycle,
    nextResponseDueAt: due.toISOString(),
    nextResponseAtRiskAt: atRisk.toISOString(),
    nextRespondedAt: null,
  };
}

/** Spec 045: the cycle stops counting while the conversation waits for the contact. */
export function pauseSlaCycle(cycle: SlaCycle, at: Date): SlaCycle {
  if (cycle.pausedAt !== null || cycle.resolvedAt !== null) return cycle;
  return { ...cycle, pausedAt: at.toISOString() };
}

/**
 * Spec 045: back to running; every pending target moves by the time paused (business minutes
 * when the policy counts them), the met ones stay. `pausedSeconds` adds the clock time.
 */
export function resumeSlaCycle(
  cycle: SlaCycle,
  at: Date,
  pausedMinutes: number,
  addMinutes: AddMinutes,
): SlaCycle {
  if (cycle.pausedAt === null) return cycle;
  const clockSeconds = Math.max(
    0,
    Math.round((at.getTime() - new Date(cycle.pausedAt).getTime()) / 1000),
  );
  const push = (instant: string | null, completedAt: string | null): string | null => {
    if (instant === null || completedAt !== null || pausedMinutes <= 0) return instant;
    return addMinutes(new Date(instant), pausedMinutes)?.toISOString() ?? instant;
  };
  return {
    ...cycle,
    pausedAt: null,
    pausedSeconds: cycle.pausedSeconds + clockSeconds,
    firstResponseDueAt: push(cycle.firstResponseDueAt, cycle.firstRespondedAt),
    firstResponseAtRiskAt: push(cycle.firstResponseAtRiskAt, cycle.firstRespondedAt),
    nextResponseDueAt: push(cycle.nextResponseDueAt, cycle.nextRespondedAt),
    nextResponseAtRiskAt: push(cycle.nextResponseAtRiskAt, cycle.nextRespondedAt),
    resolutionDueAt: push(cycle.resolutionDueAt, cycle.resolvedAt),
    resolutionAtRiskAt: push(cycle.resolutionAtRiskAt, cycle.resolvedAt),
  };
}

/** Rule 24: the state of one target at `now`. */
export function targetState(
  target: { dueAt: string; atRiskAt: string; completedAt: string | null },
  now: Date,
): SlaState {
  if (target.completedAt !== null) return target.completedAt <= target.dueAt ? 'met' : 'breached';
  const time = now.getTime();
  if (time >= new Date(target.dueAt).getTime()) return 'breached';
  if (time >= new Date(target.atRiskAt).getTime()) return 'at_risk';
  return 'on_track';
}

const WORST: readonly SlaState[] = ['breached', 'at_risk', 'on_track'];

/** The conversation's state from its targets: the worst still open; all met in time → met. */
function worstOf(targets: readonly (SlaTarget | null)[]): SlaState {
  const open = targets.filter(
    (target): target is SlaTarget => target !== null && target.state !== 'met',
  );
  return open.length === 0
    ? 'met'
    : (WORST.find((candidate) => open.some((target) => target.state === candidate)) ??
        'on_track');
}

/**
 * Rules 24 and 25: the targets of a cycle and the conversation's state — the worst of the
 * pending targets and of those met late; with all met in time, `met`. Paused (spec 045), the
 * clock stops at `pausedAt`: the targets read as they did then, the conversation is `paused`
 * unless something already broke, and there is no next deadline.
 */
export function presentSla(
  cycle: SlaCycle | null,
  policy: { id: string; name: string } | null,
  now: Date,
): ConversationSla | null {
  if (!cycle || !policy) return null;
  const paused = cycle.pausedAt !== null && cycle.resolvedAt === null;
  const moment = paused && cycle.pausedAt ? new Date(cycle.pausedAt) : now;
  const target = (
    dueAt: string | null,
    atRiskAt: string | null,
    completedAt: string | null,
  ): SlaTarget | null => {
    if (!dueAt || !atRiskAt) return null;
    return {
      dueAt,
      atRiskAt,
      completedAt,
      state: targetState({ dueAt, atRiskAt, completedAt }, moment),
    };
  };
  const firstResponse = target(
    cycle.firstResponseDueAt,
    cycle.firstResponseAtRiskAt,
    cycle.firstRespondedAt,
  );
  const nextResponse = target(
    cycle.nextResponseDueAt,
    cycle.nextResponseAtRiskAt,
    cycle.nextRespondedAt,
  );
  const resolution = target(cycle.resolutionDueAt, cycle.resolutionAtRiskAt, cycle.resolvedAt);
  if (!firstResponse && !nextResponse && !resolution) return null;
  const targets = [
    { kind: 'first-response' as const, value: firstResponse },
    { kind: 'next-response' as const, value: nextResponse },
    { kind: 'resolution' as const, value: resolution },
  ].flatMap(({ kind, value }) => (value ? [{ kind, value }] : []));
  const next = targets.find(({ value }) => value.completedAt === null) ?? null;
  const worst = worstOf([firstResponse, nextResponse, resolution]);
  const state: SlaState = paused && worst !== 'breached' && worst !== 'met' ? 'paused' : worst;
  return {
    policy,
    state,
    startedAt: cycle.startedAt,
    target: next?.kind ?? null,
    dueAt: paused ? null : (next?.value.dueAt ?? null),
    atRiskAt: paused ? null : (next?.value.atRiskAt ?? null),
    firstResponse,
    nextResponse,
    resolution,
    pausedAt: paused ? cycle.pausedAt : null,
  };
}

/** A new cycle from an inbound message (rule 22): nothing met yet. */
export function newSlaCycle(startedAt: string): SlaCycle {
  return {
    policyId: null,
    startedAt,
    firstResponseDueAt: null,
    firstResponseAtRiskAt: null,
    firstRespondedAt: null,
    nextResponseDueAt: null,
    nextResponseAtRiskAt: null,
    nextRespondedAt: null,
    resolutionDueAt: null,
    resolutionAtRiskAt: null,
    resolvedAt: null,
    pausedAt: null,
    pausedSeconds: 0,
    notified: [],
  };
}

/** The notification key of a target: the next response carries the deadline it was planned with. */
export function slaNoticeKey(
  kind: SlaTargetKind,
  state: 'at_risk' | 'breached',
  target: { dueAt: string },
): string {
  return kind === 'next-response' ? `${kind}:${state}@${target.dueAt}` : `${kind}:${state}`;
}

/**
 * Rule 26: the notifications due now for a cycle — each target entering risk or breach, once.
 * Returns the keys not yet in `notified`. A paused cycle never notifies (spec 045).
 */
export function dueSlaNotifications(
  sla: ConversationSla | null,
  notified: readonly string[],
): { target: SlaTargetKind; state: 'at_risk' | 'breached'; key: string }[] {
  if (!sla || sla.pausedAt !== null) return [];
  const due: { target: SlaTargetKind; state: 'at_risk' | 'breached'; key: string }[] = [];
  const check = (kind: SlaTargetKind, target: SlaTarget | null) => {
    if (!target || target.completedAt !== null) return;
    if (target.state !== 'at_risk' && target.state !== 'breached') return;
    const key = slaNoticeKey(kind, target.state, target);
    // A breach skips the risk notice that never went out.
    if (!notified.includes(key)) due.push({ target: kind, state: target.state, key });
  };
  check('first-response', sla.firstResponse);
  check('next-response', sla.nextResponse);
  check('resolution', sla.resolution);
  return due;
}

/** For ordering by deadline (`sort=sla`): the next due first, those without a deadline last. */
export function compareSlaDue(a: string | null, b: string | null): number {
  if (a && b) return a.localeCompare(b);
  if (a) return -1;
  if (b) return 1;
  return 0;
}

/**
 * The SLA as it reads at `now` (the screens tick between fetches): each target's state again
 * from its instants, and the conversation's (rule 24). Paused, nothing moves.
 */
export function refreshSla(sla: ConversationSla, now: Date): ConversationSla {
  if (sla.pausedAt !== null) return sla;
  const again = (target: SlaTarget | null): SlaTarget | null =>
    target ? { ...target, state: targetState(target, now) } : null;
  const firstResponse = again(sla.firstResponse);
  const nextResponse = again(sla.nextResponse);
  const resolution = again(sla.resolution);
  return {
    ...sla,
    firstResponse,
    nextResponse,
    resolution,
    state: worstOf([firstResponse, nextResponse, resolution]),
  };
}
